import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState, useEffect } from "react";
import navBarStyles from "../ApplicationMenu/ApplicationMenu.module.css";
import Button from "../Button";
import Modal from "../Modal";
import { classNames, widthInPixelsFromCssValue } from "../../utils";
import styles from "./Tooltip.module.css";
// As currently implemented (not sure if this is intentional):
// "top" arrowDirection means the tooltip content will be beneath its child
// "bottom/closeBottom/bottomRight" arrowDirections mean the tooltip content will be above its child
// "left" arrowDirection means tooltip content will be to the left of its child
// "right" arrowDirection means tooltip content will be to the right of its child
const Tooltip = ({ content, simpleContent, childrenWrapperClassName, tooltipTextClassName, extraClassNames = [], children, arrowDirection = "left", hoverOverride = false, underline = true, bottomStyle = false, translateXTooltip = "", disableAutoAdjust, 
// Use if you wish the Tooltip to be initiated by click rather than hover.
// OLD ------  clickableKey?: string; should be a unique string for the event to determine when clicking outside
// CLICKABLE KEY WILL NO LONGER EXIST AFTER ORG LEVEL OPERATIONS GOES INTO PROD
clickableKey, isClickable = false, isIcon = false, autoCloseDuration, testId, }) => {
    const [isMoreInfoShowing, setIsMoreInfoShowing] = useState(false);
    const [isShowingClickable, setIsShowingClickable] = useState(false);
    const tooltipRef = useRef(null);
    const openModal = () => setIsMoreInfoShowing(true);
    const closeModal = () => setIsMoreInfoShowing(false);
    const handleClickOutside = (event) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
            setIsShowingClickable(false);
        }
    };
    useEffect(() => {
        {
            /*
             Auto-Close Functionality:
            This useEffect hook initiates a timer when the tooltip is opened (isShowingClickable is true)
            and a valid autoCloseDuration value is provided. The timer will automatically set
            isShowingClickable to false (closing the tooltip) after the specified duration.
            
            The timer is cleared each time the tooltip's open state (isShowingClickable) changes, including
            when the component is unmounted. This is to ensure that only one timer is active at any time,
            and to prevent memory leaks from timers running on unmounted components.
            
            This provides an optional auto-close functionality for the tooltip: if the autoCloseDuration prop
            is provided, the tooltip will automatically close after the specified number of seconds.
            If autoCloseDuration is not provided, the tooltip will remain open as previously designed.
        */
        }
        let timerId;
        if (isClickable &&
            isShowingClickable &&
            typeof autoCloseDuration === "number") {
            timerId = setTimeout(() => setIsShowingClickable(false), autoCloseDuration * 1000);
        }
        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [isShowingClickable, autoCloseDuration]);
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    });
    const adjustedArrowDirection = useAdjustedArrowDirection(tooltipRef);
    const arrowDirectionToDisplay = disableAutoAdjust || adjustedArrowDirection === null
        ? arrowDirection
        : adjustedArrowDirection;
    return (_jsxs("div", Object.assign({ className: classNames(styles.tooltip, styles[arrowDirectionToDisplay], hoverOverride ? styles.hoverOverride : "", underline ? "" : styles.underlineOverride, isClickable ? styles.clickable : styles.hoverable, isShowingClickable ? styles.clickableVisible : "", ...extraClassNames), style: { bottom: "0%" }, onClick: () => {
            if (isClickable) {
                setIsShowingClickable((isShowing) => !isShowing);
            }
        }, "data-testid": testId }, { children: [_jsx("div", Object.assign({ className: classNames(!isIcon && styles.tooltipComment, childrenWrapperClassName) }, { children: children }), void 0), content !== undefined && simpleContent === undefined && (_jsx("div", Object.assign({ ref: tooltipRef, className: classNames(styles.tooltipText, styles[arrowDirectionToDisplay], translateXTooltip !== "" ? styles[translateXTooltip] : "", tooltipTextClassName), style: bottomStyle ? { bottom: "unset", marginTop: "5px" } : {}, "data-testid": "tooltip-content" }, { children: content }), void 0)), simpleContent !== undefined &&
                renderSimpleContent(simpleContent, isMoreInfoShowing, openModal, closeModal, tooltipRef, translateXTooltip, tooltipTextClassName, arrowDirectionToDisplay)] }), void 0));
};
const useAdjustedArrowDirection = (tooltipRef) => {
    // This is needed to avoid a bug where after the the direction is fixed, when
    // another render happens it sees the tooltip in an OK location, it was then
    // setting adjustedDirection to `null` and using the direction provided via the prop
    const [adjustedArrowDirection, setAdjustedArrowDirection] = useState(null);
    if (tooltipRef.current) {
        const navBarWidth = widthInPixelsFromCssValue(navBarStyles.applicationMenuWidth);
        const labelBounds = tooltipRef.current.getBoundingClientRect();
        let leftStartsOccluded = false, topStartsOccluded = false;
        if (labelBounds.left < navBarWidth) {
            leftStartsOccluded = true;
        }
        if (labelBounds.top < 0) {
            topStartsOccluded = true;
        }
        if ((leftStartsOccluded || topStartsOccluded) &&
            adjustedArrowDirection === null) {
            setAdjustedArrowDirection("right");
        }
    }
    return adjustedArrowDirection;
};
const renderSimpleContent = (simpleContent, isMoreInfoShowing, openModal, closeModal, tooltipRef, translateXTooltip, tooltipTextClassName, arrowDirectionToDisplay) => {
    const contentToMap = Array.isArray(simpleContent)
        ? simpleContent
        : [simpleContent];
    return (_jsx("div", Object.assign({ ref: tooltipRef, className: classNames(styles.tooltipText, styles[arrowDirectionToDisplay], translateXTooltip !== "" ? styles[translateXTooltip] : "", tooltipTextClassName, 
        // TODO: reinstate extra width option after fixing tooltip overlap
        // simpleContent.description.length > 160
        //   ? styles['simpleContentContainer-ExtraWidth'] :
        styles["simpleContentContainer-NormalWidth"], styles.simpleContentContainer), "data-testid": "tooltip-simple-content" }, { children: contentToMap.map(({ title, description, link, metadata, moreInfo }) => {
            let descriptionContent;
            if (typeof description === "string") {
                descriptionContent = (_jsxs("p", Object.assign({ className: styles.simpleContentDescription }, { children: [description, " ", link] }), void 0));
            }
            else {
                descriptionContent = (_jsxs(_Fragment, { children: [description, " ", link] }, void 0));
            }
            return (_jsxs(React.Fragment, { children: [_jsx("div", Object.assign({ className: styles.simpleContentTitle }, { children: title }), void 0), _jsx(_Fragment, { children: descriptionContent }, void 0), _jsxs(_Fragment, { children: [metadata !== undefined && (_jsx("p", Object.assign({ className: styles.simpleContentMetadata }, { children: metadata }), void 0)), moreInfo !== undefined && (_jsx("div", Object.assign({ className: styles.moreInfoButton, onClick: openModal }, { children: "More Information" }), void 0))] }, void 0), isMoreInfoShowing && (_jsx(Modal, Object.assign({ actions: [
                            _jsx(Button, Object.assign({ onClick: closeModal }, { children: "Close" }), "closeMoreInfo"),
                        ], onClose: closeModal }, { children: _jsx("div", Object.assign({ className: styles.modalText }, { children: moreInfo }), void 0) }), void 0))] }, `${title}-${description}`));
        }) }), void 0));
};
export default Tooltip;
